import React from 'react';
import { Group } from '@visx/group';
import { LinePath } from '@visx/shape';
import { scaleLinear } from '@visx/scale';
import { Area } from '@visx/shape';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { GridRows, GridColumns } from '@visx/grid';
import { imcBoys } from "./data/imcBoys"
import { imcGirls } from './data/imcGirls';


export const background = '#fff';

const minAge = 5
const maxAge = 228 / 12

// scales
const xScale = scaleLinear({
    domain: [minAge, maxAge]
});

const yScale = scaleLinear({
    domain: [12, 30]
});

const xTicks = [];

for (let age = minAge; age <= maxAge; age++) {
    xTicks.push(age);  // Add the age itself
    if (age < maxAge) {   // Only add intermediates if it's not the last age
        xTicks.push(age + 0.33);
        xTicks.push(age + 0.66);
    }
}


const yTicks = []

for (let imc = 12; imc <= 30; imc++) {
    yTicks.push(imc)
}

const defaultMargin = { top: 8, right: 10, bottom: 70, left: 24 };


const keys = ['P3', 'P15', 'P50', 'P85', 'P97'];

const keyLabelPosition = (key, data) => {
    const offset = 1;
    let imcPos = data[data.length - 1][key]
    return yScale(imcPos - offset)
}


export default function ImcChart({ width, height, margin = defaultMargin, gender, imc, age }) {

    let data;
    if (gender == "male") {
        data = imcBoys
    } else if (gender == "female") {
        data = imcGirls
    } else {
        return
    }

    if (width < 10) return null;

    // bounds
    const xMax = width - margin.left - margin.right;
    const yMax = height - margin.top - margin.bottom;

    xScale.range([0, xMax]);
    yScale.range([yMax, 0]);

    let showCircle = age !== undefined && imc >= 12 && imc <= 30;


    return (
        <>
            <div className='mx-auto'>
                <svg width={width} height={height}>
                    <rect x={0} y={0} width={width} height={height} fill={background} rx={14} />
                    <Group left={margin.left} top={margin.top}>
                        <rect
                            x={0}
                            y={0}
                            width={width}
                            height={height}
                            fill={background}
                            rx={14}
                        />
                        <Area
                            top={margin.top}
                            left={margin.left}
                            keys={keys}
                            data={data}
                            x={(d) => xScale(d.Month / 12)}
                            y0={() => yScale(12)}
                            y1={d => yScale(d.P3)}
                            fill="#fee2e2"

                        />

                        <Area
                            top={margin.top}
                            left={margin.left}
                            keys={keys}
                            data={data}
                            x={(d) => xScale(d.Month / 12)}
                            y0={(d) => yScale(d.P3)}
                            y1={d => yScale(d.P15)}
                            fill="#ffedd5"

                        />



                        <Area
                            top={margin.top}
                            left={margin.left}
                            keys={keys}
                            data={data}
                            x={(d) => xScale(d.Month / 12)}
                            y0={(d) => yScale(d.P15)}
                            y1={d => yScale(d.P50)}
                            fill="#dcfce7"

                        />



                        <Area
                            top={margin.top}
                            left={margin.left}
                            keys={keys}
                            data={data}
                            x={(d) => xScale(d.Month / 12)}
                            y0={(d) => yScale(d.P50)}
                            y1={d => yScale(d.P85)}
                            fill="#dcfce7"

                        />

                        <Area
                            top={margin.top}
                            left={margin.left}
                            keys={keys}
                            data={data}
                            x={(d) => xScale(d.Month / 12)}
                            y0={(d) => yScale(d.P85)}
                            y1={d => yScale(d.P97)}
                            fill="#ffedd5"

                        />

                        <Area
                            top={margin.top}
                            left={margin.left}
                            keys={keys}
                            data={data}
                            x={(d) => xScale(d.Month / 12)}
                            y0={(d) => yScale(d.P97)}
                            y1={() => yScale(30)}
                            fill="#fee2e2"

                        />


                        <GridRows scale={yScale} width={xMax} height={yMax} stroke="#f9fafb" strokeWidth={0.5} tickValues={yTicks} />
                        <GridColumns scale={xScale} width={xMax} height={yMax} stroke="#f9fafb" strokeWidth={0.5} tickValues={xTicks} />
                        <LinePath

                            top={margin.top}
                            left={margin.left}
                            keys={keys}
                            data={data}
                            x={(d) => xScale(d.Month / 12)}
                            y={(d) => yScale(d.P50)}
                            stroke="#22c55e"
                            strokeWidth={1.5}
                            strokeOpacity={1}
                        />

                        <AxisBottom
                            top={yMax}
                            scale={xScale}
                        />
                        <AxisLeft scale={yScale} tickValues={yTicks} />

                        <text
                            x={xScale(6)}
                            y={yScale(21)}
                            fontSize={14}
                            transform={`rotate(-90, ${xScale(6)}, ${yScale(21)})`}
                            alignmentBaseline="top" // Aligns the end of the text (right side when not rotated) to the y position
                        >
                            IMC
                        </text>


                        <text x={xScale(12)} y={yScale(10.5)} textAnchor="middle" fontSize={14}>
                            Age
                        </text>

                        <text x={xMax - 5} y={keyLabelPosition("P3", data)} fontSize={14} textAnchor="end">
                            P3
                        </text>
                        <text x={xMax - 5} y={keyLabelPosition("P15", data)} fontSize={14} textAnchor="end">
                            P15
                        </text>
                        <text x={xMax - 5} y={keyLabelPosition("P50", data)} fontSize={14} textAnchor="end">
                            P50
                        </text>
                        <text x={xMax - 5} y={keyLabelPosition("P85", data)} fontSize={14} textAnchor="end">
                            P85
                        </text>
                        <text x={xMax - 5} y={keyLabelPosition("P97", data)} fontSize={14} textAnchor="end">
                            P97
                        </text>

                        {showCircle && (<circle
                            cx={xScale(age)}
                            cy={yScale(imc)}
                            r={5}
                            fill="red"
                            className='drop-shadow-xl'
                        />)}


                    </Group>
                </svg>
            </div >
        </>
    );
}
